/**
 * Strike-window + Expiration-count + GEX/VEX toggle.
 *
 * Keyboard:
 *   1 → ±15 strikes
 *   2 → ±30 strikes
 *   3 → ALL strikes
 *   g/G → GEX  ·  v/V → VEX
 *
 * Expiration count (3/5/10/ALL) is mouse-only — no shortcut. Most
 * users want to set this once and forget; the keyboard map is
 * reserved for the per-session strike width adjustment.
 */

import React, { useEffect } from 'react';
import { ViewMode } from '../../lib/types';
import { StrikeWindow } from '../../hooks/useStrikeWindow';
import { ExpirationLimit } from '../../hooks/useExpirationLimit';

interface HeatmapControlsProps {
  window: StrikeWindow;
  onWindowChange: (w: StrikeWindow) => void;
  expLimit: ExpirationLimit;
  onExpLimitChange: (n: ExpirationLimit) => void;
  viewMode: ViewMode;
  onViewChange: (v: ViewMode) => void;
}

const HeatmapControls: React.FC<HeatmapControlsProps> = ({
  window, onWindowChange,
  expLimit, onExpLimitChange,
  viewMode, onViewChange,
}) => {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      switch (e.key) {
        case '1': onWindowChange(15); break;
        case '2': onWindowChange(30); break;
        case '3': onWindowChange(0); break;
        case 'g': case 'G': onViewChange('gex'); break;
        case 'v': case 'V': onViewChange('vex'); break;
      }
    };
    globalThis.addEventListener('keydown', handler);
    return () => globalThis.removeEventListener('keydown', handler);
  }, [onWindowChange, onViewChange]);

  const winBtn = (val: StrikeWindow, label: string) => (
    <button
      type="button"
      data-window={val}
      className={`gv-window-btn${window === val ? ' active' : ''}`}
      onClick={() => onWindowChange(val)}
      aria-pressed={window === val}
    >
      {label}
    </button>
  );

  const expBtn = (val: ExpirationLimit, label: string) => (
    <button
      type="button"
      data-exp-limit={val}
      className={`gv-window-btn${expLimit === val ? ' active' : ''}`}
      onClick={() => onExpLimitChange(val)}
      aria-pressed={expLimit === val}
    >
      {label}
    </button>
  );

  return (
    <div className="gv-heatmap-controls" role="toolbar" aria-label="Heatmap controls">
      <div className="gv-window-group" role="group" aria-label="Strike window">
        <span className="gv-window-label">STRIKES</span>
        {winBtn(15, '±15')}
        {winBtn(30, '±30')}
        {winBtn(0, 'ALL')}
      </div>

      <div className="gv-window-group" role="group" aria-label="Expirations to display">
        <span className="gv-window-label">EXPS</span>
        {expBtn(3, '3')}
        {expBtn(5, '5')}
        {expBtn(10, '10')}
        {expBtn(0, 'ALL')}
      </div>

      <div className="gv-view-group" role="group" aria-label="Exposure view">
        <button
          type="button"
          className={`gv-view-btn${viewMode === 'gex' ? ' active' : ''}`}
          onClick={() => onViewChange('gex')}
          aria-pressed={viewMode === 'gex'}
        >
          <span aria-hidden="true">⚡</span> GEX
        </button>
        <button
          type="button"
          className={`gv-view-btn${viewMode === 'vex' ? ' active' : ''}`}
          onClick={() => onViewChange('vex')}
          aria-pressed={viewMode === 'vex'}
        >
          <span aria-hidden="true">∿</span> VEX
        </button>
      </div>
    </div>
  );
};

export default HeatmapControls;
